import React, { useState, useEffect } from 'react';
import SourceManager from '../components/SourceManager';
import ContentFeed from '../components/ContentFeed';
import AnalysisProcesses from '../components/AnalysisProcesses';
import Statistics from '../components/Statistics';
import { API_ENDPOINTS } from '../config/api';

const ContentMonitoringPage = () => {
  const [activeTab, setActiveTab] = useState('sources');
  const [sources, setSources] = useState([]);
  const [content, setContent] = useState([]);
  const [analysisProcesses, setAnalysisProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalSources: 0,
    totalContent: 0,
    pendingAnalysis: 0,
    lastCheck: null
  });

  const tabs = [
    { id: 'sources', label: 'Sources', icon: '🔗', description: 'Manage YouTube channels and websites' },
    { id: 'content', label: 'Content Feed', icon: '📰', description: 'View monitored content' },
    { id: 'analysis', label: 'Analysis', icon: '🧠', description: 'Manage analysis processes' },
    { id: 'stats', label: 'Statistics', icon: '📊', description: 'Monitoring statistics' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch sources, content, and analysis processes
      await Promise.all([
        fetchSources(),
        fetchContent(),
        fetchAnalysisProcesses(),
        fetchStats()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSources = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CONTENT_SOURCES);
      if (response.ok) {
        const data = await response.json();
        setSources(data);
      } else {
        console.error('Failed to fetch sources');
        setSources([]);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
      setSources([]);
    }
  };

  const fetchContent = async () => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(API_ENDPOINTS.CONTENT);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else {
        console.error('Failed to fetch content');
        setContent([]);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setContent([]);
    }
  };



  const fetchAnalysisProcesses = async () => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(API_ENDPOINTS.ANALYSIS_PROCESSES);
      if (response.ok) {
        const data = await response.json();
        setAnalysisProcesses(data);
      } else {
        console.error('Failed to fetch analysis processes');
        setAnalysisProcesses([]);
      }
    } catch (error) {
      console.error('Error fetching analysis processes:', error);
      setAnalysisProcesses([]);
    }
  };

  const fetchStats = async () => {
    // TODO: Replace with actual API call
    setStats({
      totalSources: sources.length,
      totalContent: content.length,
      pendingAnalysis: content.filter(c => c.analysis_status === 'pending').length,
      lastCheck: new Date().toISOString()
    });
  };

  const handleContentUpdate = (updatedContent) => {
    setContent(updatedContent);
    fetchStats();
  };

  const handleSourcesUpdate = () => {
    fetchSources();
    fetchStats();
  };

  const handleProcessUpdate = (updatedProcesses) => {
    setAnalysisProcesses(updatedProcesses);
  };

  const runMonitoring = async () => {
    setLoading(true);
    try {
      // Call backend API to run monitoring
      const response = await fetch(API_ENDPOINTS.RUN_MONITORING, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        console.log('Content monitoring started successfully');
        // Wait a bit for processing, then refresh data
        await new Promise(resolve => setTimeout(resolve, 3000));
        await fetchData(); // Refresh data
      } else {
        console.error('Failed to start content monitoring');
      }
    } catch (error) {
      console.error('Error running monitoring:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading content monitoring...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Run Monitoring Button */}
      <div className="flex justify-end p-6 border-b border-gray-200">
        <button
          onClick={runMonitoring}
          disabled={loading}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>{loading ? 'Running...' : 'Run Monitoring'}</span>
        </button>
      </div>



      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'sources' && (
          <SourceManager 
            sources={sources} 
            onUpdate={handleSourcesUpdate}
            onRefresh={fetchSources}
          />
        )}
        {activeTab === 'content' && (
          <ContentFeed 
            content={content} 
            onUpdate={handleContentUpdate}
            channels={sources.filter(s => s.source_type === 'youtube')}
          />
        )}
        {activeTab === 'analysis' && (
          <AnalysisProcesses 
            processes={analysisProcesses} 
            onUpdate={handleProcessUpdate}
            channels={sources.filter(s => s.source_type === 'youtube')}
            websites={sources.filter(s => s.source_type !== 'youtube')}
          />
        )}
        {activeTab === 'stats' && (
          <Statistics 
            stats={stats}
            channels={sources.filter(s => s.source_type === 'youtube')}
            content={content}
          />
        )}
      </div>
    </div>
  );
};

export default ContentMonitoringPage;
